/**
 * Edit Company Profile Screen
 *
 * This page edits the COMPANY/STARTUP information.
 * For editing personal user info, use /edit-profile.
 */

import { useState } from 'react';
import { View, Text, ScrollView, TextInput } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Building2,
  MapPin,
  Globe,
  Calendar,
  Users,
  DollarSign,
  Save,
  Check,
  Linkedin,
  Target,
  TrendingUp,
  BarChart3,
  Lightbulb,
  UserCheck,
  ToggleLeft,
  ToggleRight,
  Rocket,
  Award,
  X,
  Plus,
} from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSaveCompanyProfile } from '@/lib/profile/use-save-company-profile';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { handleError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';

// Breadcrumbs for navigation
const BREADCRUMBS: Breadcrumb[] = [
  { label: 'Company Profile', href: '/(startup)/company' },
  { label: 'Edit Company' },
];

const INDUSTRIES = [
  'SaaS / Software',
  'Fintech',
  'Healthcare',
  'E-commerce',
  'AI / ML',
  'Consumer',
  'Enterprise',
  'Marketplace',
  'Hardware',
  'Other',
];

const STAGES = [
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series-a', label: 'Series A' },
] as const;

export default function EditCompanyScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { alert } = useAlert();
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const { startupId } = useMyStartup();
  const saveCompany = useSaveCompanyProfile(startupId);

  // Form state - company info
  const [name, setName] = useState(startupProfile?.name || '');
  const [tagline, setTagline] = useState(startupProfile?.tagline || '');
  const [industry, setIndustry] = useState(startupProfile?.industry || '');
  const [stage, setStage] = useState<'pre-seed' | 'seed' | 'series-a'>(
    startupProfile?.stage || 'pre-seed'
  );
  const [location, setLocation] = useState(startupProfile?.location || '');
  const [website, setWebsite] = useState(startupProfile?.website || '');
  const [linkedIn, setLinkedIn] = useState(startupProfile?.linkedIn || '');
  const [teamSize, setTeamSize] = useState(startupProfile?.teamSize?.toString() || '1');
  const [foundedYear, setFoundedYear] = useState(
    startupProfile?.foundedYear?.toString() || new Date().getFullYear().toString()
  );
  const [fundingTarget, setFundingTarget] = useState(
    startupProfile?.fundingTarget?.toString() || ''
  );

  // Fundraising details
  const [isActivelyRaising, setIsActivelyRaising] = useState(
    startupProfile?.isActivelyRaising ?? true
  );

  // Business - Problem/Solution
  const [problem, setProblem] = useState(startupProfile?.problem || '');
  const [solution, setSolution] = useState(startupProfile?.solution || '');
  const [targetCustomer, setTargetCustomer] = useState(startupProfile?.targetCustomer || '');

  // Traction
  const [monthlyRevenue, setMonthlyRevenue] = useState(
    startupProfile?.monthlyRevenue?.toString() || ''
  );
  const [userCount, setUserCount] = useState(startupProfile?.userCount?.toString() || '');
  const [growthRate, setGrowthRate] = useState(startupProfile?.growthRate?.toString() || '');

  // Market
  const [marketSize, setMarketSize] = useState(startupProfile?.marketSize?.toString() || '');
  const [competitors, setCompetitors] = useState<string[]>(startupProfile?.competitors || []);
  const [newCompetitor, setNewCompetitor] = useState('');

  // Founder Background
  const [isSerialFounder, setIsSerialFounder] = useState(startupProfile?.isSerialFounder ?? false);
  const [previousVentures, setPreviousVentures] = useState<string[]>(
    startupProfile?.previousVentures || []
  );
  const [newVenture, setNewVenture] = useState('');

  const [showIndustryPicker, setShowIndustryPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const formatCurrency = (value: string): string => {
    const num = value.replace(/[^0-9]/g, '');
    if (!num) return '';
    return Number(num).toLocaleString();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Required Field', 'Please enter the company name.');
      return;
    }
    if (!tagline.trim()) {
      alert('Required Field', 'Please enter a tagline.');
      return;
    }
    if (!industry) {
      alert('Required Field', 'Please select an industry.');
      return;
    }

    setIsSaving(true);
    try {
      // THE SERVER, not a timer. This handler carried an explicit
      // "// Simulate save delay", awaited setTimeout(resolve, 800) and then
      // wrote only to the persisted Zustand store — so every company edit was
      // lost to every other device and to the server, and vanished at sign-out.
      // The single write path is src/lib/profile/use-save-company-profile.ts;
      // authorization is the team-membership RLS policy, not a check here.
      await saveCompany.mutateAsync({
        name: name.trim(),
        tagline: tagline.trim(),
        industry,
        stage,
        location: location.trim(),
        website: website.trim(),
        linkedIn: linkedIn.trim(),
        teamSize: parseInt(teamSize) || undefined,
        foundedYear: parseInt(foundedYear) || undefined,
        fundingTarget: parseInt(fundingTarget.replace(/[^0-9]/g, '')) || undefined,
        isActivelyRaising,
        problem: problem.trim(),
        solution: solution.trim(),
        targetCustomer: targetCustomer.trim(),
        monthlyRevenue: parseInt(monthlyRevenue.replace(/[^0-9]/g, '')) || undefined,
        userCount: parseInt(userCount.replace(/[^0-9]/g, '')) || undefined,
        growthRate: parseFloat(growthRate) || undefined,
        marketSize: parseInt(marketSize.replace(/[^0-9]/g, '')) || undefined,
        competitors: competitors.length > 0 ? competitors : undefined,
        isSerialFounder,
        previousVentures: previousVentures.length > 0 ? previousVentures : undefined,
      });
    } catch (err) {
      // A failed save must not report success or navigate away.
      const { message, isCancelled } = handleError(err, { context: 'EditCompany.handleSave' });
      if (!isCancelled) alert("Couldn't save your company", message);
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      router.back();
    }, 1000);
  };

  const content = (
    <>
      {/* Form */}
      <Animated.View entering={FadeInDown.delay(100).duration(500)} className="gap-4">
        {/* Company Name */}
        <FormField
          label="Company Name"
          icon={<Building2 size={20} color={colors.text.tertiary} />}
          value={name}
          onChangeText={setName}
          placeholder="Enter company name"
          required
        />

        {/* Tagline */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
            Tagline <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <TextInput
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
              borderRadius: 12,
              padding: 16,
              color: colors.text.primary,
              fontSize: 16,
              minHeight: 80,
              textAlignVertical: 'top',
            }}
            placeholder="One line that describes your startup"
            placeholderTextColor={colors.text.tertiary}
            value={tagline}
            onChangeText={setTagline}
            multiline
          />
        </View>

        {/* Industry Picker */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
            Industry <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <AnimatedPressable
            onPress={() => setShowIndustryPicker(!showIndustryPicker)}
            className="flex-row items-center rounded-xl px-4 py-4"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            <Building2 size={20} color={colors.text.tertiary} />
            <Text
              style={{
                color: industry ? colors.text.primary : colors.text.tertiary,
                marginLeft: 12,
                fontSize: 16,
              }}
            >
              {industry || 'Select industry'}
            </Text>
          </AnimatedPressable>
          {showIndustryPicker && (
            <View
              className="mt-2 rounded-xl overflow-hidden"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              {INDUSTRIES.map((ind, idx) => (
                <AnimatedPressable
                  key={ind}
                  onPress={() => {
                    setIndustry(ind);
                    setShowIndustryPicker(false);
                  }}
                  className="px-4 py-3"
                  style={{
                    borderBottomWidth: idx < INDUSTRIES.length - 1 ? 1 : 0,
                    borderBottomColor: colors.border.light,
                    backgroundColor: industry === ind ? colors.primary[50] : 'transparent',
                  }}
                >
                  <Text
                    style={{
                      color: industry === ind ? colors.primary[600] : colors.text.primary,
                    }}
                  >
                    {ind}
                  </Text>
                </AnimatedPressable>
              ))}
            </View>
          )}
        </View>

        {/* Stage */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
            Stage <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <View className="flex-row gap-2">
            {STAGES.map((s) => (
              <AnimatedPressable
                key={s.value}
                onPress={() => setStage(s.value)}
                className="flex-1 py-3 rounded-xl items-center"
                style={{
                  backgroundColor: stage === s.value ? colors.primary[600] : colors.surface.primary,
                  borderWidth: 1,
                  borderColor: stage === s.value ? colors.primary[600] : colors.border.light,
                }}
              >
                <Text
                  style={{
                    color: stage === s.value ? colors.text.inverse : colors.text.primary,
                    fontWeight: '500',
                  }}
                >
                  {s.label}
                </Text>
              </AnimatedPressable>
            ))}
          </View>
        </View>

        {/* Location */}
        <FormField
          label="Location"
          icon={<MapPin size={20} color={colors.text.tertiary} />}
          value={location}
          onChangeText={setLocation}
          placeholder="e.g., Zurich, Switzerland"
        />

        {/* Website */}
        <FormField
          label="Website"
          icon={<Globe size={20} color={colors.text.tertiary} />}
          value={website}
          onChangeText={setWebsite}
          placeholder="e.g., https://yourcompany.com"
          keyboardType="url"
          autoCapitalize="none"
        />

        {/* Team Size & Founded Row */}
        <View className="flex-row gap-4">
          <View className="flex-1">
            <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
              Team Size
            </Text>
            <View
              className="flex-row items-center rounded-xl px-4"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <Users size={20} color={colors.text.tertiary} />
              <TextInput
                style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
                placeholder="e.g., 5"
                placeholderTextColor={colors.text.tertiary}
                value={teamSize}
                onChangeText={setTeamSize}
                keyboardType="number-pad"
              />
            </View>
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
              Founded Year
            </Text>
            <View
              className="flex-row items-center rounded-xl px-4"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <Calendar size={20} color={colors.text.tertiary} />
              <TextInput
                style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
                placeholder="2024"
                placeholderTextColor={colors.text.tertiary}
                value={foundedYear}
                onChangeText={setFoundedYear}
                keyboardType="number-pad"
                maxLength={4}
              />
            </View>
          </View>
        </View>

        {/* Funding Target */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
            Funding Target
          </Text>
          <View
            className="flex-row items-center rounded-xl px-4"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            <DollarSign size={20} color={colors.text.tertiary} />
            <TextInput
              style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
              placeholder="e.g., 500,000"
              placeholderTextColor={colors.text.tertiary}
              value={fundingTarget}
              onChangeText={(text) => setFundingTarget(formatCurrency(text))}
              keyboardType="number-pad"
            />
          </View>
        </View>

        {/* LinkedIn */}
        <FormField
          label="LinkedIn Company Page"
          icon={<Linkedin size={20} color={colors.text.tertiary} />}
          value={linkedIn}
          onChangeText={setLinkedIn}
          placeholder="e.g., linkedin.com/company/yourcompany"
          autoCapitalize="none"
        />
      </Animated.View>

      {/* Fundraising Details Section */}
      <Animated.View entering={FadeInDown.delay(150).duration(500)} className="gap-4 mt-6">
        <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold uppercase tracking-wide">
          Fundraising Details
        </Text>

        {/* Minimum Ticket Size and "Syndicates OK" were REMOVED here.
            Neither has a column on `startup_profiles`, so neither could ever be
            saved — they collected input and discarded it, which is the same
            deception the fake-save fix removes. They are fundraising-ROUND
            concepts; `fundraising_rounds` models a round. Re-add them there, or
            add columns, before showing them again. */}
        {/* Toggle Options Row */}
        <View className="flex-row gap-4">
          {/* Actively Raising */}
          <AnimatedPressable
            onPress={() => setIsActivelyRaising(!isActivelyRaising)}
            className="flex-1 flex-row items-center justify-between p-4 rounded-xl"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: isActivelyRaising ? colors.traffic.green : colors.border.light,
            }}
          >
            <View className="flex-1">
              <Text style={{ color: colors.text.primary }} className="font-medium text-sm">
                Actively Raising
              </Text>
            </View>
            {isActivelyRaising ? (
              <ToggleRight size={24} color={colors.traffic.green} />
            ) : (
              <ToggleLeft size={24} color={colors.text.tertiary} />
            )}
          </AnimatedPressable>
        </View>
      </Animated.View>

      {/* Business Section */}
      <Animated.View entering={FadeInDown.delay(175).duration(500)} className="gap-4 mt-6">
        <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold uppercase tracking-wide">
          Business
        </Text>

        {/* Problem */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
            Problem You're Solving
          </Text>
          <TextInput
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
              borderRadius: 12,
              padding: 16,
              color: colors.text.primary,
              fontSize: 16,
              minHeight: 80,
              textAlignVertical: 'top',
            }}
            placeholder="What problem does your startup solve?"
            placeholderTextColor={colors.text.tertiary}
            value={problem}
            onChangeText={setProblem}
            multiline
          />
        </View>

        {/* Solution */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
            Your Solution
          </Text>
          <TextInput
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
              borderRadius: 12,
              padding: 16,
              color: colors.text.primary,
              fontSize: 16,
              minHeight: 80,
              textAlignVertical: 'top',
            }}
            placeholder="How does your product solve this problem?"
            placeholderTextColor={colors.text.tertiary}
            value={solution}
            onChangeText={setSolution}
            multiline
          />
        </View>

        {/* Target Customer */}
        <FormField
          label="Target Customer"
          icon={<UserCheck size={20} color={colors.text.tertiary} />}
          value={targetCustomer}
          onChangeText={setTargetCustomer}
          placeholder="e.g., SMBs with 10-100 employees"
        />
      </Animated.View>

      {/* Traction Section */}
      <Animated.View entering={FadeInDown.delay(200).duration(500)} className="gap-4 mt-6">
        <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold uppercase tracking-wide">
          Traction
        </Text>

        {/* Monthly Revenue */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
            Monthly Revenue (MRR)
          </Text>
          <View
            className="flex-row items-center rounded-xl px-4"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            <DollarSign size={20} color={colors.text.tertiary} />
            <TextInput
              style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
              placeholder="e.g., 15,000"
              placeholderTextColor={colors.text.tertiary}
              value={monthlyRevenue}
              onChangeText={(text) => setMonthlyRevenue(formatCurrency(text))}
              keyboardType="number-pad"
            />
          </View>
        </View>

        {/* User Count & Growth Rate Row */}
        <View className="flex-row gap-4">
          <View className="flex-1">
            <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
              Active Users
            </Text>
            <View
              className="flex-row items-center rounded-xl px-4"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <Users size={20} color={colors.text.tertiary} />
              <TextInput
                style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
                placeholder="e.g., 500"
                placeholderTextColor={colors.text.tertiary}
                value={userCount}
                onChangeText={(text) => setUserCount(text.replace(/[^0-9]/g, ''))}
                keyboardType="number-pad"
              />
            </View>
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
              Growth Rate (%)
            </Text>
            <View
              className="flex-row items-center rounded-xl px-4"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <TrendingUp size={20} color={colors.text.tertiary} />
              <TextInput
                style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
                placeholder="e.g., 15"
                placeholderTextColor={colors.text.tertiary}
                value={growthRate}
                onChangeText={setGrowthRate}
                keyboardType="decimal-pad"
              />
            </View>
          </View>
        </View>
      </Animated.View>

      {/* Market Section */}
      <Animated.View entering={FadeInDown.delay(225).duration(500)} className="gap-4 mt-6">
        <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold uppercase tracking-wide">
          Market
        </Text>

        {/* Market Size */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
            Total Addressable Market (TAM)
          </Text>
          <View
            className="flex-row items-center rounded-xl px-4"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            <BarChart3 size={20} color={colors.text.tertiary} />
            <TextInput
              style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
              placeholder="e.g., 5,000,000,000"
              placeholderTextColor={colors.text.tertiary}
              value={marketSize}
              onChangeText={(text) => setMarketSize(formatCurrency(text))}
              keyboardType="number-pad"
            />
          </View>
        </View>

        {/* Competitors */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
            Key Competitors
          </Text>
          <View className="flex-row gap-2 mb-2">
            <View
              className="flex-1 flex-row items-center rounded-xl px-4"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <Target size={20} color={colors.text.tertiary} />
              <TextInput
                style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
                placeholder="Add competitor"
                placeholderTextColor={colors.text.tertiary}
                value={newCompetitor}
                onChangeText={setNewCompetitor}
              />
            </View>
            <AnimatedPressable
              onPress={() => {
                if (newCompetitor.trim()) {
                  setCompetitors([...competitors, newCompetitor.trim()]);
                  setNewCompetitor('');
                }
              }}
              className="w-14 h-14 rounded-xl items-center justify-center"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <Plus size={24} color="white" />
            </AnimatedPressable>
          </View>
          {competitors.length > 0 && (
            <View className="flex-row flex-wrap gap-2">
              {competitors.map((comp, idx) => (
                <View
                  key={idx}
                  className="flex-row items-center gap-2 px-3 py-2 rounded-lg"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <Text style={{ color: colors.text.primary }} className="text-sm">
                    {comp}
                  </Text>
                  <AnimatedPressable onPress={() => setCompetitors(competitors.filter((_, i) => i !== idx))}>
                    <X size={16} color={colors.text.tertiary} />
                  </AnimatedPressable>
                </View>
              ))}
            </View>
          )}
        </View>
      </Animated.View>

      {/* Founder Background Section */}
      <Animated.View entering={FadeInDown.delay(250).duration(500)} className="gap-4 mt-6">
        <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold uppercase tracking-wide">
          Founder Background
        </Text>

        {/* Serial Founder Toggle */}
        <AnimatedPressable
          onPress={() => setIsSerialFounder(!isSerialFounder)}
          className="flex-row items-center justify-between p-4 rounded-xl"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: isSerialFounder ? colors.amber.main : colors.border.light,
          }}
        >
          <View className="flex-row items-center gap-3">
            <Award size={20} color={isSerialFounder ? colors.amber.main : colors.text.tertiary} />
            <View>
              <Text style={{ color: colors.text.primary }} className="font-medium">
                Serial Founder
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                Previously founded other companies
              </Text>
            </View>
          </View>
          {isSerialFounder ? (
            <ToggleRight size={24} color={colors.amber.main} />
          ) : (
            <ToggleLeft size={24} color={colors.text.tertiary} />
          )}
        </AnimatedPressable>

        {/* Previous Ventures (only show if serial founder) */}
        {isSerialFounder && (
          <View>
            <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
              Previous Ventures
            </Text>
            <View className="flex-row gap-2 mb-2">
              <View
                className="flex-1 flex-row items-center rounded-xl px-4"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                }}
              >
                <Rocket size={20} color={colors.text.tertiary} />
                <TextInput
                  style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
                  placeholder="Add previous company"
                  placeholderTextColor={colors.text.tertiary}
                  value={newVenture}
                  onChangeText={setNewVenture}
                />
              </View>
              <AnimatedPressable
                onPress={() => {
                  if (newVenture.trim()) {
                    setPreviousVentures([...previousVentures, newVenture.trim()]);
                    setNewVenture('');
                  }
                }}
                className="w-14 h-14 rounded-xl items-center justify-center"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <Plus size={24} color="white" />
              </AnimatedPressable>
            </View>
            {previousVentures.length > 0 && (
              <View className="flex-row flex-wrap gap-2">
                {previousVentures.map((venture, idx) => (
                  <View
                    key={idx}
                    className="flex-row items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: colors.amber.light }}
                  >
                    <Text style={{ color: colors.text.primary }} className="text-sm">
                      {venture}
                    </Text>
                    <AnimatedPressable onPress={() => setPreviousVentures(previousVentures.filter((_, i) => i !== idx))}>
                      <X size={16} color={colors.text.tertiary} />
                    </AnimatedPressable>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}
      </Animated.View>

      {/* Save Button */}
      <Animated.View entering={FadeInDown.delay(200).duration(500)} className="mt-8">
        <AnimatedPressable
          onPress={handleSave}
          disabled={isSaving}
          className="flex-row items-center justify-center gap-2 py-4 rounded-xl"
          style={{
            backgroundColor: saved ? colors.success : colors.primary[600],
          }}
        >
          {saved ? (
            <>
              <Check size={20} color="white" />
              <Text className="text-white font-semibold">Saved!</Text>
            </>
          ) : (
            <>
              <Save size={20} color="white" />
              <Text className="text-white font-semibold">
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Text>
            </>
          )}
        </AnimatedPressable>
      </Animated.View>
    </>
  );

  // Web layout with AppLayout
  if (isWeb) {
    return (
      <AppLayout title="Edit Company Profile" breadcrumbs={BREADCRUMBS}>
        <KeyboardAwareBody
          className="flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ padding: 20, maxWidth: 600 }}
        >
          {content}
        </KeyboardAwareBody>
      </AppLayout>
    );
  }

  // Mobile layout
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top']} className="flex-1">
        {/* Header */}
        <Animated.View
          entering={FadeInDown.delay(100).duration(500)}
          className="flex-row items-center justify-between px-5 pt-4 pb-4"
          style={{
            backgroundColor: colors.surface.primary,
            borderBottomWidth: 1,
            borderBottomColor: colors.border.light,
          }}
        >
          <View className="flex-row items-center gap-4">
            <AnimatedPressable
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </AnimatedPressable>
            <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
              Edit Company
            </Text>
          </View>
        </Animated.View>

        <KeyboardAwareBody
          className="flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ padding: 20 }}
        >
          {content}
        </KeyboardAwareBody>
      </SafeAreaView>
    </View>
  );
}

function FormField({
  label,
  icon,
  value,
  onChangeText,
  placeholder,
  keyboardType = 'default',
  autoCapitalize = 'sentences',
  required,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  keyboardType?: 'default' | 'email-address' | 'numeric' | 'url' | 'number-pad';
  autoCapitalize?: 'none' | 'sentences' | 'words';
  required?: boolean;
}) {
  const colors = useThemeColors();
  return (
    <View>
      <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
        {label} {required && <Text style={{ color: colors.red.main }}>*</Text>}
      </Text>
      <View
        className="flex-row items-center rounded-xl px-4"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {icon}
        <TextInput
          style={{ flex: 1, paddingVertical: 16, paddingLeft: 12, color: colors.text.primary, fontSize: 16 }}
          placeholder={placeholder}
          placeholderTextColor={colors.text.tertiary}
          value={value}
          onChangeText={onChangeText}
          keyboardType={keyboardType}
          autoCapitalize={autoCapitalize}
        />
      </View>
    </View>
  );
}
